import { type ReactNode, useContext } from 'react';

import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import type { User } from '@/@types/global';
import { RenderLogo } from '@/app/appHeader/helpers';
import { ExternalPatientContext } from '@/app/external/constants';
import { useGetDateFNSLocale } from '@/locales/helpers';
import { usePreferences } from '@/react-query/usePreferences';
import { useGetCountry } from '@/utils/internationalization/useGetCountry';
import { mixpanelEvents, useTrackEventWithOptionalParams } from '@/utils/mixpanel';
import { parseAndFormatDate } from '@/utils/parseDate';

import { latestImagesVisitDateAtom } from '../../atoms';
import {
  previewDisplayDeviceAtom,
  previewModeAtom,
} from '../../multiFileViewer/imageToolbar/atoms';
import {
  patientDownloadableNotesAtom,
  patientDownloadableSelectedTreatmentsAtom,
  selectedTeethPreviewAtom,
} from '../atoms';

import { useFindingsHidden } from './hooks/useFindingsHidden';
import { TreatmentsText } from './TreatmentOptions';

interface PatientPrintoutLayoutProps {
  children: ReactNode;
  scale?: number;
}

export const PatientPrintoutLayout = ({
  children,
  scale = 1,
}: PatientPrintoutLayoutProps) => {
  const previewDisplayDevice = useAtomValue(previewDisplayDeviceAtom);
  const previewMode = useAtomValue(previewModeAtom);
  const visitDate = useAtomValue(latestImagesVisitDateAtom);
  const clinicalNote = useAtomValue(patientDownloadableNotesAtom);
  const selectedTeethPreview = useAtomValue(selectedTeethPreviewAtom);
  const defaultSelectedTreatments = useAtomValue(
    patientDownloadableSelectedTreatmentsAtom,
  );

  const externalPatientContext = useContext(ExternalPatientContext);
  const { id: selectedPatientId } = useParams<{ id: string }>();
  const patientId = selectedPatientId ?? externalPatientContext.patientId;
  const patients = useSelector((state: any) => state.annotator?.patients) || {};
  const currentPatient = patients[patientId] || {};
  const user: User = useSelector((state: any) => state?.login?.user);
  const { data: preferences } = usePreferences();
  const dateFNSLocale = useGetDateFNSLocale();
  const isCanadianPractice = useGetCountry() === 'CA';
  const { trackEvent } = useTrackEventWithOptionalParams({ id: patientId });
  const isFindingsHidden = useFindingsHidden();
  const { t } = useLingui();

  const SALES_DEMO_BOOKING_URL = 'https://www.videa.ai/demo';
  const bookingUrl = process.env.VITE_SALES_DEMO_ENV
    ? SALES_DEMO_BOOKING_URL
    : preferences?.qrCodeBookingUrl;
  const hasBookingUrl = !!bookingUrl;
  const practiceName =
    (process.env.VITE_SALES_DEMO_ENV &&
      user.salesDemoPracticeConfig?.practiceName) ||
    user.primaryPracticeName;

  const isMobile = previewDisplayDevice === 'mobile';
  const isPrintoutForTreatments = previewMode !== 'qrCode';

  return (
    <div
      className="print-container vai-w-full vai-overflow-visible vai-origin-top-left"
      style={{ transform: `scale(${scale})` }}
      data-testid="patient-printout-layout"
    >
      <style>{`@page { margin: 0.75in 0; }`}</style>
      {practiceName && (
        <div
          className={classNames(
            'vai-border-b vai-border-slate-200 vai-mb-4 vai-flex vai-flex-wrap vai-items-center vai-justify-between vai-gap-2',
            {
              'vai-px-8 vai-pb-4': !isMobile,
              'vai-px-4 vai-pt-6 vai-pb-3': isMobile,
            },
          )}
        >
          <p
            className={classNames(
              'vai-font-inter vai-font-semibold vai-text-[#061E3E]',
              { 'vai-text-3xl': !isMobile, 'vai-text-2xl': isMobile },
            )}
          >
            {practiceName}
          </p>
          {isMobile && hasBookingUrl && (
            <a
              className="vai-inline-flex vai-items-center vai-gap-1.5 vai-font-inter vai-text-sm vai-font-medium vai-text-sapphire-600 vai-bg-sapphire-50 vai-px-3 vai-py-1.5 vai-rounded-full vai-no-underline hover:vai-bg-sapphire-100 vai-transition-colors"
              href={bookingUrl}
              rel="noreferrer"
              target="_blank"
              onClick={() =>
                trackEvent(mixpanelEvents.qrBookingLinkClick, {
                  bookingUrl,
                })
              }
            >
              <FontAwesomeIcon icon={faCalendar} className="vai-text-xs" />
              <Trans>Book Appointment</Trans>
            </a>
          )}
        </div>
      )}
      <div
        className={classNames('vai-pb-8', {
          'vai-px-10': !isMobile,
          'vai-px-4': isMobile,
        })}
      >
        <RenderLogo color="#061E3E" />

        {!isMobile && currentPatient.name && (
          <p className="vai-font-inter vai-text-sm vai-mb-4 vai-uppercase vai-my-3">
            {currentPatient.name}
          </p>
        )}

        <div className="vai-flex vai-justify-between vai-items-center vai-gap-2 vai-my-3">
          <p
            className={classNames('vai-font-inter vai-font-semibold', {
              'vai-text-2xl': !isMobile,
              'vai-text-xl': isMobile,
            })}
          >
            <Trans>Patient Report</Trans>
          </p>
          {visitDate && (
            <p className="vai-font-inter vai-font-semibold">
              {parseAndFormatDate(
                visitDate.toISOString(),
                'MMM. d, yyyy',
                dateFNSLocale,
              )}
            </p>
          )}
        </div>
        <p
          className={classNames('vai-font-inter vai-text-zinc-600 vai-mb-4', {
            'vai-text-[12px]': isMobile,
          })}
        >
          <Trans>Your dental practice has analyzed your x-rays with</Trans>{' '}
          {isCanadianPractice ? t`clinically validated` : t`FDA-cleared`}{' '}
          <Trans>
            AI algorithms from VideaAI. This is a copy of the findings for your
            records. These findings are not direct diagnoses.
          </Trans>
        </p>

        {clinicalNote && (
          <>
            <p className="vai-font-inter vai-font-semibold vai-text-zinc-900 vai-mb-4">
              <Trans>Clinician Note</Trans>
            </p>
            <p className="vai-font-inter vai-mb-4">{clinicalNote}</p>
          </>
        )}

        {children}

        {/* Disclaimer section */}
        {(!!isFindingsHidden || !!selectedTeethPreview.length) && (
          <p className="vai-font-inter vai-text-sm vai-text-zinc-600 vai-my-4">
            <Trans>
              * Note: some analyzed findings may not be displayed in image and
              summary above
            </Trans>
          </p>
        )}

        {/* Treatments section */}
        {!!defaultSelectedTreatments.length && (
          <div>
            <p className="vai-font-inter vai-font-semibold vai-text-zinc-900 vai-my-4">
              <Trans>Treatment</Trans>
            </p>

            <div className="vai-flex vai-flex-col vai-gap-6">
              {defaultSelectedTreatments.map((treatment) => (
                <TreatmentsText
                  className={classNames('vai-break-inside-avoid', {
                    '[&:nth-of-type(even)]:vai-flex-row-reverse': !isMobile,
                    'vai-flex-col': isMobile,
                  })}
                  key={treatment}
                  showDiagram={true}
                  treatment={treatment}
                  isPrintout={isPrintoutForTreatments}
                />
              ))}
            </div>
          </div>
        )}

        {/* Booking CTA card — shown on mobile QR view when practice has a booking URL */}
        {isMobile && hasBookingUrl && (
          <div className="vai-bg-sapphire-50 vai-rounded-xl vai-p-4 vai-mt-6 vai-flex vai-flex-col vai-items-center vai-gap-2 vai-text-center">
            <div className="vai-flex vai-items-center vai-gap-2">
              <FontAwesomeIcon
                icon={faCalendar}
                className="vai-text-sapphire-600"
              />
              <p className="vai-font-inter vai-font-semibold vai-text-zinc-900">
                <Trans>Ready to Book?</Trans>
              </p>
            </div>
            <p className="vai-font-inter vai-text-sm vai-text-zinc-600">
              <Trans>Schedule your next appointment:</Trans>{' '}
              <a
                className="vai-font-inter vai-text-sm vai-font-semibold vai-text-sapphire-600 vai-underline vai-cursor-pointer"
                href={bookingUrl}
                rel="noreferrer"
                target="_blank"
                onClick={() =>
                  trackEvent(mixpanelEvents.qrBookingLinkClick, {
                    bookingUrl,
                  })
                }
              >
                <Trans>Book Now</Trans>
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
